import { cn } from '@/lib/utils';
import type {
  SectionCompletion,
  SectionProgressEntry,
  TemplateSection,
} from '@/types/section-guidance';
import { CheckCircle2, Circle, CircleDot } from 'lucide-react';
import { useEffect, useRef } from 'react';

interface SectionBadgesHorizontalProps {
  sections: TemplateSection[];
  activeSectionIndex: number;
  entries: SectionProgressEntry[];
  onSectionClick: (index: number) => void;
}

const CompletionIcon = ({ completion }: { completion: SectionCompletion }) => {
  switch (completion) {
    case 'complete':
      return <CheckCircle2 className="text-primary size-4" />;
    case 'partial':
      return <CircleDot className="size-4 text-yellow-600" />;
    case 'incomplete':
    default:
      return <Circle className="size-4 text-gray-400" />;
  }
};

export const SectionBadgesHorizontal = ({
  sections,
  activeSectionIndex,
  entries,
  onSectionClick,
}: SectionBadgesHorizontalProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const getCompletionForSection = (index: number): SectionCompletion => {
    const entry = entries.find((e) => e.sectionIndex === index);
    return entry?.completion ?? 'incomplete';
  };

  // Auto-scroll to active badge when it changes
  useEffect(() => {
    const el = scrollContainerRef.current?.children[activeSectionIndex];
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [activeSectionIndex]);

  return (
    <div
      ref={scrollContainerRef}
      className="scrollbar-none flex gap-2 overflow-x-auto pb-2">
      {sections.map((section, index) => {
        const isActive = index === activeSectionIndex;
        const completion = getCompletionForSection(index);

        return (
          <button
            key={index}
            onClick={() => onSectionClick(index)}
            className={cn(
              'flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-all',
              isActive
                ? 'border-primary bg-primary/5 text-primary-border'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
            )}>
            <CompletionIcon completion={completion} />
            <span className="whitespace-nowrap">{section.title}</span>
          </button>
        );
      })}
    </div>
  );
};
